import { CSSProperties, Fragment, ReactNode } from 'react';
import Skeleton from 'react-loading-skeleton';
import styled from 'styled-components';

import { Text } from '../../cdl/Text/Text';
import { theme } from '../ui/theme';

type DataSheetColumns = 1 | 2;

const DataSheetGrid = styled.div<{ $columns: DataSheetColumns }>`
    display: grid;
    align-items: start;
    grid-template-columns: ${({ $columns }) =>
        $columns === 2 ? 'min-content 1fr min-content 1fr' : 'min-content 1fr'};
    grid-column-gap: ${({ $columns }) => ($columns === 2 ? theme.space[48] : theme.space[24])};
    grid-row-gap: ${theme.space[8]};
    height: max-content;
`;

interface DataSheetProps {
    children: ReactNode;
    columns?: DataSheetColumns;
}

export const DataSheet = ({ columns = 1, ...props }: DataSheetProps) => {
    return <DataSheetGrid $columns={columns} {...props} />;
};

interface DataSheetLabelProps {
    children: string;
}

const DataSheetLabel = (props: DataSheetLabelProps) => {
    return <Text style={{ whiteSpace: 'nowrap' }} color="foreground.muted" variant="fieldLabel" {...props} />;
};

DataSheet.Label = DataSheetLabel;

interface DataSheetHeadingProps {
    children: string;
    marginTop?: CSSProperties['marginTop'];
}

const DataSheetHeading = (props: DataSheetHeadingProps) => {
    return (
        <Text variant="subtitle2" style={{ whiteSpace: 'nowrap', gridColumn: '1 / -1', marginTop: props.marginTop }}>
            {props.children}
        </Text>
    );
};

DataSheet.Heading = DataSheetHeading;

interface DataSheetSkeletonProps {
    rows: number;
}

const DataSheetSkeleton = ({ rows }: DataSheetSkeletonProps) => {
    return (
        <DataSheet>
            {Array.from(Array(rows)).map((_, index) => {
                return (
                    <Fragment key={index}>
                        <Skeleton width="100px" />
                        <Skeleton width="250px" />
                    </Fragment>
                );
            })}
        </DataSheet>
    );
};

DataSheet.Skeleton = DataSheetSkeleton;

interface DataSheetRowProps {
    label: string;
    value: string | undefined | null;
}

const DataSheetRow = ({ label, value }: DataSheetRowProps) => {
    if (value === undefined) {
        return null;
    }
    return (
        <>
            <DataSheet.Label>{label}</DataSheet.Label>
            <Text>{value}</Text>
        </>
    );
};

DataSheet.Row = DataSheetRow;
